import {createBrowserRouter} from '@datadog/browser-rum-react/react-router-v6';
import TrackLayout from 'Layout';
import ProtectedRoute from 'ProtectedRoute';
import {ConfigProvider} from 'antd';
import {handleApiError} from 'middleware/errorHandler';
import {useSelector} from 'react-redux';
import {Navigate} from 'react-router-dom';
import {RouterProvider, useRouteError} from 'react-router-dom';
import {selectIsBootstrapped, selectIsLoggedIn} from 'store/authSlice';
import {theme} from 'theme';
import {IS_DASHBOARD_MODE} from 'utils/consts';
import errorLogger, {createTrackError} from 'utils/errors';
import {lazyWithRetry} from 'utils/lazyWithRetry';

const AuditsPage = lazyWithRetry(() => import('pages/AuditsPage'), 'audits');
const LoginPage = lazyWithRetry(() => import('pages/AuthLoginPage'), 'login');
const DashboardHome = lazyWithRetry(
  () => import('pages/DashboardHome'),
  'dashboard-home'
);
const DashboardPage = lazyWithRetry(
  () => import('pages/DashboardPage'),
  'data-pilot'
);
const DataPortalOverviewPage = lazyWithRetry(
  () => import('pages/DataPortalOverviewPage'),
  'data-portal-overview'
);
const DataPortalPage = lazyWithRetry(
  () => import('pages/DataPortalPage'),
  'data-portal'
);
const DataReportOverviewPage = lazyWithRetry(
  () => import('pages/DataReportOverviewPage'),
  'data-report-overview'
);
const DataReportPage = lazyWithRetry(
  () => import('pages/DataReportPage'),
  'data-report'
);
const EditorCatalogPage = lazyWithRetry(
  () => import('pages/EditorCatalogPage'),
  'editor-catalog'
);
const EditorPage = lazyWithRetry(() => import('pages/EditorPage'), 'editor');
const EsoyITSupportPage = lazyWithRetry(
  () => import('pages/EsoyITSupportPage'),
  'esoy-it-support'
);
const EsoyOverview = lazyWithRetry(
  () => import('pages/EsoyOverviewPage'),
  'esoy-overview'
);
const HSSchedulePage = lazyWithRetry(
  () => import('pages/HSSchedulePage'),
  'hs-schedule'
);
const NotFoundPage = lazyWithRetry(
  () => import('pages/NotFoundPage'),
  'not-found'
);
const PersonMappingPage = lazyWithRetry(
  () => import('pages/PersonMappingPage'),
  'person-mapping'
);
const RetiredRoutePage = lazyWithRetry(
  () => import('pages/RetiredRoutePage'),
  'retired-route'
);
const SearchPage = lazyWithRetry(() => import('pages/SearchPage'), 'search');
const SeedEditorPage = lazyWithRetry(
  () => import('pages/SeedEditorPage'),
  'seed-editor'
);
const SeedsOverviewPage = lazyWithRetry(
  () => import('pages/SeedsOverviewPage'),
  'seeds-overview'
);
const Sitemap = lazyWithRetry(() => import('pages/Sitemap'), 'sitemap');
const StaffRolesITSupportPage = lazyWithRetry(
  () => import('pages/StaffRolesITSupportPage'),
  'staff-roles-it-support'
);
const StaffRolesPage = lazyWithRetry(
  () => import('pages/StaffRolesPage'),
  'staff-roles'
);
const TagsAndCategoriesPage = lazyWithRetry(
  () => import('pages/TagsAndCategoriesPage'),
  'tags-categories'
);
const TrackHome = lazyWithRetry(() => import('pages/TrackHome'), 'home');
const UCPersonDetailsPage = lazyWithRetry(
  () => import('pages/UCPersonDetailsPage'),
  'person-details'
);
const UCPersonPage = lazyWithRetry(
  () => import('pages/UCPersonPage'),
  'person'
);

function RouterErrorBoundary() {
  const error = useRouteError();

  // Convert router error to TrackError with proper context
  const trackError = createTrackError(error, {
    status: error?.status || 511,
    context: {
      errorType: 'router',
      url: window.location.href,
      pathname: window.location.pathname,
      routerError: true,
      timestamp: new Date().toISOString(),
    },
  });

  // Log the router error
  errorLogger.logError(trackError, {
    context: {
      errorType: 'router',
      url: window.location.href,
      originalError: error,
    },
  });

  // Handle via standard error handler (will show appropriate UI)
  handleApiError(trackError);

  // Return null since handleApiError will show the error UI
  return null;
}

function DashboardModeCatchAll() {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isBootstrapped = useSelector(selectIsBootstrapped);

  // Wait for the /auth/me/ bootstrap check before deciding where to route
  if (!isBootstrapped) return null;

  if (isLoggedIn) {
    return <Navigate to="/portal/8" replace />;
  }
  return <Navigate to="/" replace />;
}

const appRoutes = [
  {path: '/sitemap', element: <Sitemap />},
  {
    path: '/datapilot',
    element: <RetiredRoutePage targetApp="Uncommon Dashboards" />,
  },
  {
    path: '/datapilot/:slug',
    element: <RetiredRoutePage targetApp="Uncommon Dashboards" />,
  },
  {
    path: '/audits',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/audits/:school',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/audit_rules',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/audits_overview',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/audits_issue_overview',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/audits_school_summary',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/audits_school_summary/:school',
    element: <ProtectedRoute component={AuditsPage} />,
  },
  {
    path: '/data_report',
    element: <ProtectedRoute component={DataReportOverviewPage} />,
  },
  {
    path: '/data_report/:report',
    element: <ProtectedRoute component={DataReportPage} />,
  },
  {
    path: '/data_report/editor',
    element: <ProtectedRoute component={DataReportOverviewPage} />,
  },
  {
    path: '/data_report/:report/editor',
    element: <ProtectedRoute component={DataReportPage} />,
  },
  {
    path: '/hs_schedule',
    element: <ProtectedRoute component={HSSchedulePage} />,
  },
  {
    path: '/hs_schedule/:school',
    element: <ProtectedRoute component={HSSchedulePage} />,
  },
  {
    path: '/person/mapping',
    element: <ProtectedRoute component={PersonMappingPage} />,
  },
  {
    path: '/person',
    element: <ProtectedRoute component={UCPersonPage} />,
  },
  {
    path: '/person/:ucpid',
    element: <ProtectedRoute component={UCPersonDetailsPage} />,
  },
  {
    path: '/school_setup',
    element: <ProtectedRoute component={EsoyOverview} />,
  },
  {
    path: '/school_setup/:school',
    element: <ProtectedRoute component={EsoyOverview} />,
  },
  {
    path: '/staff_roles',
    element: <ProtectedRoute component={StaffRolesPage} />,
  },
  {
    path: '/staff_roles/:school',
    element: <ProtectedRoute component={StaffRolesPage} />,
  },
  {
    path: '/staff_roles_it_support',
    element: <ProtectedRoute component={StaffRolesITSupportPage} />,
  },
  {
    path: '/esoy_it_support',
    element: <ProtectedRoute component={EsoyITSupportPage} />,
  },
  {
    path: '/source_manager/editor',
    element: <ProtectedRoute component={SeedsOverviewPage} />,
  },
  {
    path: '/source_manager/:source',
    element: <ProtectedRoute component={SeedEditorPage} />,
  },
  {
    path: '/source_manager/:source/editor',
    element: <ProtectedRoute component={SeedEditorPage} />,
  },
  {
    path: '/source_manager',
    element: <ProtectedRoute component={SeedsOverviewPage} />,
  },
  {
    path: '/data_portal',
    element: <ProtectedRoute component={DataPortalOverviewPage} />,
  },
  {
    path: '/data_portal/editor',
    element: <ProtectedRoute component={DataPortalOverviewPage} />,
  },
  {
    path: '/data_portal/:page',
    element: <ProtectedRoute component={DataPortalPage} />,
  },
  {
    path: '/data_portal/:page/editor',
    element: <ProtectedRoute component={DataPortalPage} />,
  },
  {
    path: '/search',
    element: <ProtectedRoute component={SearchPage} />,
  },
  {
    path: '/search_chat',
    element: <ProtectedRoute component={SearchPage} />,
  },
  {
    path: '/tags_categories',
    element: <ProtectedRoute component={TagsAndCategoriesPage} />,
  },
  {
    path: '/editorjs',
    element: <ProtectedRoute component={EditorCatalogPage} />,
  },
  {
    path: '/editorjs/editor',
    element: <ProtectedRoute component={EditorCatalogPage} />,
  },
  {
    path: '/editorjs/:id',
    element: <ProtectedRoute component={EditorPage} />,
  },
  {
    path: '/editorjs/:id/editor',
    element: <ProtectedRoute component={EditorPage} />,
  },
];

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <TrackLayout simplified={IS_DASHBOARD_MODE} />,
      children: [
        {
          errorElement: <RouterErrorBoundary />,
          children: [
            {
              index: true,
              element: IS_DASHBOARD_MODE ? <DashboardHome /> : <TrackHome />,
            },
            {path: '/login', element: <LoginPage />},
            {path: '/login_done', element: <LoginPage />},
            {path: '/login_failed', element: <LoginPage />},
            ...(IS_DASHBOARD_MODE
              ? [
                  {
                    path: '/portal/:slug',
                    element: <ProtectedRoute component={DashboardPage} />,
                  },
                ]
              : appRoutes),
            {
              path: '*',
              element: IS_DASHBOARD_MODE ? (
                <DashboardModeCatchAll />
              ) : (
                <NotFoundPage />
              ),
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
      v7_startTransition: true,
    },
  }
);

function App() {
  return (
    <ConfigProvider theme={theme}>
      <title>{IS_DASHBOARD_MODE ? 'Uncommon Dashboards' : 'TRACK - App'}</title>
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default App;
